import React, { useState, useEffect } from 'react';
import { 
  FiCheckCircle, 
  FiAlertTriangle, 
  FiX, 
  FiRotateCw, 
  FiExternalLink,
  FiCalendar,
  FiFileText,
  FiCheck
} from 'react-icons/fi';
import { backgroundTaskManager } from '../crm/services/backgroundTaskManager';

export default function BackgroundTaskToasts() {
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    const unsubscribe = backgroundTaskManager.subscribe((updatedTasks) => {
      setTasks(updatedTasks);
    });
    return () => unsubscribe();
  }, []);

  if (!tasks || tasks.length === 0) return null;

  return (
    <div className="fixed bottom-5 right-5 z-[99999] flex flex-col gap-2.5 max-w-md w-full pointer-events-none px-3">
      {tasks.map((task) => {
        const isRunning = task.status === 'running';
        const isCompleted = task.status === 'completed';
        const isFailed = task.status === 'failed';

        return (
          <div
            key={task.id}
            className={`pointer-events-auto rounded-xl p-3.5 transition-all shadow-xl border text-xs animate-in slide-in-from-bottom-3 duration-200 ${
              isFailed
                ? 'bg-white border-rose-300 ring-2 ring-rose-500/20'
                : isCompleted
                ? 'bg-white border-emerald-300 ring-1 ring-emerald-500/20'
                : 'bg-white border-[#d3d6df] shadow-2xl'
            }`}
          >
            {/* Header row */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2.5 min-w-0">
                {/* Icon box */}
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 border ${
                    isFailed
                      ? 'bg-rose-50 text-rose-600 border-rose-200'
                      : isCompleted
                      ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                      : 'bg-sky-50 text-sky-600 border-sky-200'
                  }`}
                >
                  {isRunning ? (
                    <div className="w-4 h-4 border-2 border-sky-600 border-t-transparent rounded-full animate-spin" />
                  ) : isCompleted ? (
                    <FiCheckCircle className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <FiAlertTriangle className="w-4 h-4 text-rose-600" />
                  )}
                </div>

                {/* Text details */}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-[#161a2e] truncate block">
                      {task.title}
                    </span>
                    <span
                      className={`text-[9px] px-1.5 py-0.2 rounded-full font-bold uppercase tracking-wider shrink-0 border ${
                        isRunning
                          ? 'bg-sky-50 text-sky-700 border-sky-200'
                          : isCompleted
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-rose-50 text-rose-700 border-rose-200'
                      }`}
                    >
                      {isRunning ? 'Background' : isCompleted ? 'Success' : 'Failed'}
                    </span>
                  </div>

                  <div className="mt-0.5 text-[11px]">
                    {isRunning && (
                      <span className="text-[#575e74] flex items-center gap-1.5">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-500 animate-ping" />
                        Processing in background... carry on working
                      </span>
                    )}
                    {isCompleted && (
                      <span className="text-emerald-700 font-medium">
                        {task.successMessage || 'Operation completed successfully.'}
                      </span>
                    )}
                    {isFailed && (
                      <span className="text-rose-700 font-medium break-words">
                        {task.error || 'Operation failed. Please review and retry.'}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Dismiss / Close button */}
              <button
                type="button"
                onClick={() => backgroundTaskManager.dismissTask(task.id)}
                className="text-[#8c94a8] hover:text-[#161a2e] p-1 rounded-md hover:bg-slate-100 transition-colors cursor-pointer shrink-0"
                title="Dismiss"
              >
                <FiX className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Actions Footer for failed or link */}
            {(isFailed || task.actionLink) && (
              <div className="flex items-center justify-end gap-2 pt-2.5 mt-2.5 border-t border-[#f1f2f6]">
                {task.actionLink && (
                  <a
                    href={task.actionLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:text-emerald-800 font-bold underline"
                  >
                    <span>{task.actionLabel || 'View Event'}</span>
                    <FiExternalLink className="w-3 h-3" />
                  </a>
                )}
                {isFailed && (
                  <button
                    type="button"
                    onClick={() => backgroundTaskManager.retryTask(task.id)}
                    className="flex items-center gap-1 px-3 py-1 bg-rose-600 hover:bg-rose-500 text-white rounded-lg text-xs font-bold transition shadow-xs cursor-pointer"
                  >
                    <FiRotateCw className="w-3 h-3" />
                    <span>Retry Now</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => backgroundTaskManager.dismissTask(task.id)}
                  className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-[#575e74] rounded-lg text-xs font-semibold transition cursor-pointer"
                >
                  Dismiss
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
